import { ApiResponse } from '@shared/types';
import {
  PaymentMethodAccountMappingRecord,
  SavePaymentMethodAccountMappingRequest,
} from '../types/accounting';
import { supabase } from './supabase';

const mapPaymentMethodAccountMapping = (
  value: Record<string, unknown>,
): PaymentMethodAccountMappingRecord => ({
  id: String(value.id || ''),
  company_id: Number(value.company_id || 0),
  payment_method_id: Number(value.payment_method_id || 0),
  account_id: String(value.account_id || ''),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

export const fetchPaymentMethodAccountMappings = async (
  companyId: number,
): Promise<ApiResponse<PaymentMethodAccountMappingRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('payment_method_account_mappings' as never)
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) =>
        mapPaymentMethodAccountMapping(item as Record<string, unknown>),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch payment mappings',
      data: [],
    };
  }
};

export const savePaymentMethodAccountMapping = async (
  input: SavePaymentMethodAccountMappingRequest,
): Promise<ApiResponse<PaymentMethodAccountMappingRecord>> => {
  const payload = {
    company_id: input.companyId,
    payment_method_id: input.paymentMethodId,
    account_id: input.accountId,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('payment_method_account_mappings' as never)
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return {
        success: true,
        data: mapPaymentMethodAccountMapping(data as Record<string, unknown>),
      };
    }

    const { data, error } = await supabase
      .from('payment_method_account_mappings' as never)
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: mapPaymentMethodAccountMapping(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save payment mapping',
      data: undefined,
    };
  }
};

export const deletePaymentMethodAccountMapping = async (
  companyId: number,
  mappingId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('payment_method_account_mappings' as never)
      .delete()
      .eq('company_id', companyId)
      .eq('id', mappingId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete payment mapping',
      data: null,
    };
  }
};
